import React, { memo, useMemo } from 'react';
import styled from 'styled-components';

import Button from '../../../Button';
import Bubble from '../../../Bubble';

import ToggleButtonsGroup from '../../../ToggleButtonsGroup';

import {
  Call,
  Mail,
  Note,
  Message,
  Messanger,
  Group,
  Bot,
  System,
  User,
  Task,
  Debug,
  SystemAuto,
  SystemView,
  SystemChange,
  SystemDispatch,
  UserRating,
  TourSelection,
  UserCreationAction,
} from '@icons/communication';

import {
  LOG_RELATIONS,
  CLIENT_LOG_TYPES as ClientLogTypes,
  TICKET_LOG_TYPES as TicketLogTypes,
  CLIENT_LOG_TOOLTIPS,
  TICKET_LOG_TOOLTIPS,
  HIDDEN_LOG_TYPES,
} from '../../../../constants';
import { ValueOf } from '@utils/tsUtils';

const { StyledButton } = ToggleButtonsGroup;

const availableIcons = {
  [ClientLogTypes.CALL]: <Call width={15} height={15} />,
  [ClientLogTypes.EMAIL]: <Mail width={16} height={16} />,
  [ClientLogTypes.NOTE]: <Note width={14} height={14} />,
  [ClientLogTypes.MESSAGE]: <Messanger width={16} height={15} />,
  [ClientLogTypes.SMS]: <Message width={16} height={15} />,
  [ClientLogTypes.MESSAGE_GROUP]: <Group width={16} height={13} />,
  [ClientLogTypes.MANAGER_EMAIL]: <Bot width={16} height={15} />,

  [TicketLogTypes.NOTE]: <Note width={14} height={14} />,
  [TicketLogTypes.SYSTEM]: <System width={14} height={14} />,
  [TicketLogTypes.USER]: <User width={14} height={14} />,
  [TicketLogTypes.TASK]: <Task width={14} height={14} />,
  [TicketLogTypes.DEBUG]: <Debug width={14} height={14} />,
  [TicketLogTypes.SYSTEM_AUTO]: <SystemAuto width={14} height={14} />,
  [TicketLogTypes.SYSTEM_VIEW]: <SystemView width={14} height={14} />,
  [TicketLogTypes.SYSTEM_CHANGE]: <SystemChange width={14} height={14} />,
  [TicketLogTypes.SYSTEM_DISPATCH]: <SystemDispatch width={14} height={14} />,
  [TicketLogTypes.USER_RATING]: <UserRating width={14} height={14} />,
  [TicketLogTypes.TOUR_SELECTION]: <TourSelection width={14} height={14} />,
  [TicketLogTypes.USER_CREATION_ACTION]: (
    <UserCreationAction width={14} height={14} />
  ),
};

interface ICommunicationFilters {
  className?: string;
  relateTo: (typeof LOG_RELATIONS)[keyof typeof LOG_RELATIONS];
  values: Array<ValueOf<typeof ClientLogTypes | typeof TicketLogTypes>>;
  types: Array<ValueOf<typeof ClientLogTypes | typeof TicketLogTypes>>;
  onChange: (event: CustomEvent) => void;
}

const CommunicationFilters = ({
  relateTo,
  types,
  values,
  onChange,
  ...props
}: ICommunicationFilters) => {
  const fullTypes = useMemo(
    () =>
      relateTo === LOG_RELATIONS.TICKET
        ? (Object.values(TicketLogTypes) as Array<
            ValueOf<typeof TicketLogTypes>
          >)
        : (Object.values(ClientLogTypes) as Array<
            ValueOf<typeof ClientLogTypes>
          >),
    [relateTo]
  );

  const allowedTypesSet = useMemo(() => new Set(types), [types]);

  const buttons = useMemo(
    () =>
      fullTypes
        .map(type => {
          if (HIDDEN_LOG_TYPES.has(type)) {
            return null;
          }

          return (
            <StyledToggleButton
              key={type}
              value={type}
              alt={type}
              // disabled={!allowedTypesSet.has(type)}
            >
              <Bubble
                text={CLIENT_LOG_TOOLTIPS[type] || TICKET_LOG_TOOLTIPS[type]}
                shouldStopPropagation={false}
              >
                {availableIcons[type] || <Bot width={16} height={15} />}
              </Bubble>
            </StyledToggleButton>
          );
        })
        .filter(Boolean),
    [fullTypes, allowedTypesSet]
  );

  return (
    <StyledCommunicationFilters {...props}>
      <StyledTogglers multiple values={values} onChange={onChange}>
        {buttons}
      </StyledTogglers>
    </StyledCommunicationFilters>
  );
};

const StyledCommunicationFilters = styled.div``;

const StyledTogglers = styled(ToggleButtonsGroup)`
  ${StyledButton} {
    line-height: 40px;
    &:first-child {
      padding-left: 15px;
    }
    &:last-child {
      padding-right: 15px;
    }
  }
`;

const StyledToggleButton = styled(Button)`
  svg {
    stroke: white;

    g,
    rect,
    circle,
    path {
      stroke: #007aff;
    }
  }

  ${props =>
    props.checked &&
    `
    background: #007aff;

    svg g { stroke: white; }
    svg rect { stroke: white; }
    svg path { stroke: white; }
    svg circle { stroke: white; }
  `}

  ${props =>
    props.disabled &&
    `
      svg { opacity: .4; }
  `}
`;

export default memo(CommunicationFilters);
